import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Layout,
  Menu,
  Avatar,
  Dropdown,
  Typography,
  Space,
  Button,
} from 'antd';
import {
  DashboardOutlined,
  FileTextOutlined,
  FileDoneOutlined,
  UserOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  ShopOutlined,
  MessageOutlined,
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { CustomerWorkspaceProvider, useCustomerWorkspace } from '../contexts/CustomerWorkspaceContext';
import CustomerWorkspaceShell from './CustomerWorkspaceShell';
import PoweredByVarexe from '../components/common/PoweredByVarexe';
import './AuthenticatedLayout.css';

const { Header, Sider, Content, Footer } = Layout;
const { Text } = Typography;

const NAV_ITEMS = [
  { key: '/dashboard',       icon: <DashboardOutlined />, label: 'Dashboard'     },
  { key: '/invoices',        icon: <FileTextOutlined />,  label: 'Invoices'      },
  { key: '/estimates',       icon: <FileDoneOutlined />,  label: 'Estimates'     },
  { key: '/workspaces',      icon: <MessageOutlined />,   label: 'Workspaces'    },
];

const DESKTOP_SIDER_WIDTH = 260;
const MOBILE_BREAKPOINT = 'lg';

function AuthenticatedInnerColumn({
  children,
  collapsed,
  setCollapsed,
  userMenuItems,
  initials,
  fullName,
}) {
  const { detailSplitLayout } = useCustomerWorkspace();

  return (
    <Layout
      className={
        detailSplitLayout
          ? 'varexe-auth-main varexe-auth-main--detail-split'
          : 'varexe-auth-main'
      }
      style={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Header className="varexe-header">
        <div className="varexe-header-left">
          <Button
            variant="filled"
            color="default"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            className="varexe-collapse-btn"
          />
        </div>

        <div className="varexe-header-right">
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight" arrow>
            <Space className="varexe-user-trigger" style={{ cursor: 'pointer' }}>
              <Avatar
                size={32}
                style={{ background: 'var(--varexe-soft-primary)', color: 'var(--varexe-primary)', fontWeight: 600, fontSize: 13 }}
              >
                {initials}
              </Avatar>
              {fullName && (
                <Text style={{ fontSize: 13, fontWeight: 500 }} className="varexe-header-username">
                  {fullName}
                </Text>
              )}
            </Space>
          </Dropdown>
        </div>
      </Header>

      <Content
        className={
          detailSplitLayout
            ? 'varexe-content varexe-content--flush varexe-content--detail-split'
            : 'varexe-content varexe-content--flush'
        }
        style={{ minHeight: 'calc(100vh - 64px - 48px)' }}
      >
        <CustomerWorkspaceShell>{children}</CustomerWorkspaceShell>
      </Content>

      <Footer className="varexe-footer">
        <Text type="secondary" style={{ fontSize: 12 }}>
          Your data is protected and confidential.
        </Text>
        <PoweredByVarexe textColor="var(--varexe-gray-500)" fontSize={12} logoHeight={20} />
      </Footer>
    </Layout>
  );
}

const AuthenticatedLayout = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();
  const navigate  = useNavigate();
  const { user, organizationName, organizationLogo, logout } = useAuth();

  const selectedKey = '/' + location.pathname.split('/')[1];

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'My Profile',
      onClick: () => navigate('/profile'),
    },
    { type: 'divider' },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Sign Out',
      danger: true,
      onClick: handleLogout,
    },
  ];

  const fullName = user
    ? `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() || user.portal_email
    : '';

  const initials = fullName
    ? fullName.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase()
    : 'U';

  const orgInitial = organizationName ? organizationName[0].toUpperCase() : '?';

  useEffect(() => {
    if (isMobile) {
      setCollapsed(true);
    }
  }, [isMobile]);

  return (
    <Layout style={{ minHeight: '100vh', display: 'flex' }}>
      <Sider
        collapsible
        collapsed={collapsed}
        trigger={null}
        width={DESKTOP_SIDER_WIDTH}
        collapsedWidth={isMobile ? 0 : 80}
        breakpoint={MOBILE_BREAKPOINT}
        onBreakpoint={(broken) => setIsMobile(broken)}
        className="varexe-sider"
      >
        <div className={`varexe-sider-logo ${collapsed ? 'collapsed' : ''}`}>
          <Link to="/dashboard" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
            {organizationLogo ? (
              <img
                src={organizationLogo}
                alt={organizationName}
                className="varexe-org-logo-img"
              />
            ) : (
              <Avatar
                size={collapsed ? 32 : 36}
                style={{ background: 'var(--varexe-soft-primary)', color: 'var(--varexe-primary)', fontWeight: 700, fontSize: 16, flexShrink: 0 }}
                icon={!orgInitial ? <ShopOutlined /> : undefined}
              >
                {orgInitial}
              </Avatar>
            )}
            {!collapsed && organizationName && (
              <span className="varexe-org-name-text">{organizationName}</span>
            )}
          </Link>
        </div>

        <Menu
          mode="inline"
          selectedKeys={[selectedKey]}
          onClick={() => {
            if (isMobile) {
              setCollapsed(true);
            }
          }}
          items={NAV_ITEMS.map(item => ({
            key:   item.key,
            icon:  item.icon,
            label: <Link to={item.key}>{item.label}</Link>,
          }))}
          style={{ border: 'none', background: 'transparent' }}
        />
      </Sider>
      {isMobile && !collapsed && (
        <div
          className="varexe-sider-backdrop"
          onClick={() => setCollapsed(true)}
          aria-hidden="true"
        />
      )}

      <CustomerWorkspaceProvider>
        <AuthenticatedInnerColumn
          collapsed={collapsed}
          setCollapsed={setCollapsed}
          userMenuItems={userMenuItems}
          initials={initials}
          fullName={fullName}
        >
          {children}
        </AuthenticatedInnerColumn>
      </CustomerWorkspaceProvider>
    </Layout>
  );
};

export default AuthenticatedLayout;
